/*
  CriticalAlertsStrip — Cliniko-style red banner shown wherever an
  appointment/booking is opened, listing every active CRITICAL alert
  AND every severe/anaphylaxis allergy for that client so the
  practitioner sees them before starting a consultation.

  Renders nothing when neither exists — safe to drop at the top of any
  client-context surface.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Check, Sparkle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function CriticalAlertsStrip({ email, onAckOne }) {
  const [alerts, setAlerts] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!email) return;
    try {
      const [aR, gR] = await Promise.all([
        api.post("/health-record/critical-alerts-batch", { emails: [email] }),
        api.post("/critical-allergies-batch", { emails: [email] }),
      ]);
      const key = email.toLowerCase();
      setAlerts(aR.data?.by_email?.[key] || []);
      setAllergies(gR.data?.by_email?.[key] || []);
    } catch {
      /* silent — accessory element */
    }
  }, [email]);

  useEffect(() => { load(); }, [load]);

  async function acknowledge(id) {
    setBusyId(id);
    try {
      await api.post(`/health-record/alerts/${id}/acknowledge`);
      toast.success("Acknowledged");
      await load();
      onAckOne?.(id);
    } catch (e) {
      toast.error("Couldn't acknowledge", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusyId(null);
    }
  }

  if (alerts.length === 0 && allergies.length === 0) return null;

  return (
    <div
      className="rounded-md border border-red-400 bg-red-50 p-3 space-y-2"
      role="alert"
      data-testid="critical-alerts-strip"
    >
      <div className="flex items-center gap-2 text-red-900 text-xs font-semibold uppercase tracking-wider">
        <AlertTriangle className="h-3.5 w-3.5" />
        Critical alerts &amp; allergies
      </div>
      <ul className="space-y-1.5">
        {alerts.map((a) => (
          <li
            key={a.id}
            className="flex items-start justify-between gap-3 text-sm text-red-950"
            data-testid={`critical-alert-${a.id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{a.title}</div>
              {a.description && (
                <div className="text-[0.75rem] text-red-900/90 mt-0.5">{a.description}</div>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => acknowledge(a.id)}
              disabled={busyId === a.id}
              className="border-red-300 hover:bg-red-100 text-red-900 shrink-0"
              data-testid={`critical-alert-ack-${a.id}`}
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              {busyId === a.id ? "…" : "Acknowledge"}
            </Button>
          </li>
        ))}
        {allergies.map((g) => (
          <li
            key={g.id}
            className="flex items-start justify-between gap-3 text-sm text-red-950"
            data-testid={`critical-allergy-${g.id}`}
          >
            <div className="min-w-0 flex-1 flex items-start gap-1.5">
              <Sparkle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-red-800" />
              <div className="min-w-0">
                <div className="font-semibold">
                  {g.severity === "anaphylaxis" ? "Anaphylaxis" : "Severe allergy"} — {g.substance}
                </div>
                {(g.reaction || g.management_instructions) && (
                  <div className="text-[0.75rem] text-red-900/90 mt-0.5">
                    {g.reaction && <span>Reaction: {g.reaction}. </span>}
                    {g.management_instructions && <span>{g.management_instructions}</span>}
                  </div>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
